/**
 * EvidenceGallery — visual evidence explorer for a dossier.
 * Each item reads like a catalogued exhibit: type-stamped frame,
 * evidence date, source, license, authenticity and its connection
 * to the investigation. Distinct presentation per document type
 * while preserving the Archive design language.
 */
import React, { useState } from "react";
import { Info, FileText, Scale, Landmark, Newspaper, Map as MapIcon,
         Mail, Radio, BookOpen, Camera, ShieldCheck } from "lucide-react";
import { useRecordMedia } from "./PublishedMedia";
import { DossierLabel } from "../dossier/DossierLabel";

const API = process.env.REACT_APP_BACKEND_URL;
const full = (u) => (u && u.startsWith("/") ? `${API}${u}` : u);

/* Distinct visual identity per evidence type — accent, icon, stamp text. */
const TYPES = {
  foia:       { label: "FOIA RELEASE",   icon: FileText,  accent: "#A68F5E" },
  court:      { label: "COURT RECORD",   icon: Scale,     accent: "#5FC96C" },
  report:     { label: "OFFICIAL REPORT", icon: Landmark, accent: "#9FA8D4" },
  memo:       { label: "MEMORANDUM",     icon: FileText,  accent: "#A68F5E" },
  newspaper:  { label: "PRESS ARCHIVE",  icon: Newspaper, accent: "#D9B44A" },
  map:        { label: "MAP / CHART",    icon: MapIcon,   accent: "#6FB3A8" },
  letter:     { label: "CORRESPONDENCE", icon: Mail,      accent: "#C39BD3" },
  cable:      { label: "INTEL CABLE",    icon: Radio,     accent: "#E08B4A" },
  photograph: { label: "PHOTOGRAPH",     icon: Camera,    accent: "#8A8377" },
};

const typeOf = (img) => TYPES[img.evidence_type] || TYPES.photograph;

const EvidenceCard = ({ img }) => {
  const [meta, setMeta] = useState(false);
  const t = typeOf(img);
  const Icon = t.icon;
  const isDoc = img.evidence_type && img.evidence_type !== "photograph";
  return (
    <figure className="min-w-0 break-inside-avoid mb-4 border border-[#2D2A26] bg-[#0d0c0a]"
            data-testid={`evidence-card-${img.id}`}>
      {/* Type stamp bar */}
      <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-[#2D2A26]"
           style={{ borderLeft: `2px solid ${t.accent}` }}>
        <Icon size={10} style={{ color: t.accent }} />
        <span className="font-mono text-[8.5px] uppercase tracking-[0.25em]"
              style={{ color: t.accent }}>
          {t.label}
        </span>
        {img.evidence_date && (
          <span className="font-mono text-[8.5px] tracking-[0.2em] text-[#757065]">
            · {img.evidence_date}
          </span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 font-mono text-[8px] uppercase tracking-[0.15em]"
              style={{ color: img.verification === "verified" ? "#5FC96C" : "#757065" }}>
          <ShieldCheck size={8} />
          {img.verification === "verified" ? "Authenticated" : "On file"}
        </span>
      </div>
      {/* Exhibit */}
      <a href={full(img.urls.jpeg || img.urls.webp)} target="_blank" rel="noreferrer"
         className="block bg-black p-2">
        <img src={full(img.urls.webp || img.urls.jpeg)} alt={img.alt}
             loading="lazy" decoding="async"
             width={img.width || undefined} height={img.height || undefined}
             className="w-full h-auto max-h-[420px] object-contain"
             style={{ filter: isDoc ? "sepia(0.08) contrast(1.03)" : "sepia(0.15)" }} />
      </a>
      {/* Connection + provenance */}
      <figcaption className="px-2.5 py-2">
        {img.caption && (
          <div className="text-[11.5px] text-[#A39E93] leading-snug line-clamp-3">
            {img.caption}
          </div>
        )}
        <div className="mt-1.5 flex items-start justify-between gap-2">
          <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#5A554C]">
            {img.rights || "License on record"}
            {meta && (
              <span className="block normal-case tracking-normal text-[9px] text-[#757065] mt-1 space-y-0.5">
                {img.credit && <span className="block">Credit: {img.credit}</span>}
                {img.source && <span className="block break-all">Source: {img.source}</span>}
              </span>
            )}
          </div>
          <button type="button" onClick={() => setMeta((v) => !v)}
                  data-testid={`evidence-meta-${img.id}`}
                  title="Provenance" aria-label="Toggle provenance"
                  className="shrink-0 text-[#5A554C] hover:text-[#A68F5E] transition-colors">
            <Info size={11} />
          </button>
        </div>
      </figcaption>
    </figure>
  );
};

export const EvidenceGallery = ({ slug }) => {
  const images = useRecordMedia("dossier", slug);
  if (!images || images.length === 0) return null;
  const visible = images.filter(
    (i) => !["internal_only", "background_reference"].includes(i.role));
  if (visible.length === 0) return null;
  const docs = visible.filter((i) => i.evidence_type && i.evidence_type !== "photograph");
  const photos = visible.filter((i) => !i.evidence_type || i.evidence_type === "photograph");
  return (
    <section className="mt-12" data-testid="evidence-gallery">
      <DossierLabel as="h2" className="mt-0">Evidence Gallery</DossierLabel>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#757065] mt-2 mb-5">
        {visible.length} catalogued exhibit{visible.length !== 1 ? "s" : ""} · source and license on every item
      </div>
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-4">
        {[...docs, ...photos].map((img) => (
          <EvidenceCard key={img.id} img={img} />
        ))}
      </div>
    </section>
  );
};

export default EvidenceGallery;
